import { useT } from "../../shared/i18n";
import { Button, Sheet } from "../../shared/ui";
import type { ConflictState } from "./RecordForm";

interface ConflictField {
  fieldId: string;
  label: string;
  mine: string;
  server: string | null;
}

/**
 * Revision conflict. There is no automatic last-write-wins and no silent merge:
 * the changed fields are listed side by side and the choice is the user's.
 *
 * A bottom sheet in Telegram, a modal on desktop — same content either way.
 */
export function ConflictSheet({
  open,
  onOpenChange,
  conflict,
  changedFields,
  onReload,
  onKeepMine,
  onDiscardMine,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conflict: ConflictState;
  changedFields: ConflictField[];
  onReload: () => void;
  onKeepMine: () => void;
  onDiscardMine: () => void;
}) {
  const t = useT();
  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      title={t("conflict.title")}
      description={t("conflict.body")}
      footer={
        <>
          <Button variant="secondary" onClick={onReload}>
            {t("conflict.reload")}
          </Button>
          <Button variant="secondary" onClick={onKeepMine}>
            {t("conflict.keepMine")}
          </Button>
          <Button variant="destructive" onClick={onDiscardMine}>
            {t("conflict.discardMine")}
          </Button>
        </>
      }
    >
      <p className="mb-3 font-mono text-mono text-fg-subtle">
        {t("conflict.revision")}: {conflict.current.revision}
      </p>
      <dl className="flex flex-col gap-2">
        {changedFields.map((field) => (
          <div
            key={field.fieldId}
            className="border-b border-line-subtle pb-2 last:border-b-0"
          >
            <dt className="text-caption font-semibold text-fg-muted">
              {field.label}
            </dt>
            <dd className="text-body text-fg">
              <span className="text-accent-fg">{t("conflict.mine")}:</span>{" "}
              {field.mine || t("conflict.emptyValue")}
            </dd>
            <dd className="text-body text-fg">
              <span className="text-fg-muted">{t("conflict.server")}:</span>{" "}
              {field.server ?? t("conflict.unchanged")}
            </dd>
          </div>
        ))}
      </dl>
    </Sheet>
  );
}
